import * as Header from '-/Custom/Header';
import { Stack, useRouter } from 'expo-router';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { type FragmentOf, readFragment } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import useRootStore, { openDrawer } from '@/stores/useRootStore';
import { OrganizationDetailToolbarFragment } from './OrganizationDetailToolbarFragment';

function OrganizationDetailToolbar({
  fragment,
}: {
  fragment:
    | FragmentOf<typeof OrganizationDetailToolbarFragment>
    | null
    | undefined;
}) {
  const organizationId = useRootStore((state) => state.organizationId);
  const personId = useUserId();

  const { navigate } = useRouter();
  const event = readFragment(OrganizationDetailToolbarFragment, fragment);

  const actions = {
    edit: event?.permissions?.edit,
    leave: event?.owner_id !== personId && event?.event_people?.length === 1,
  };

  const showActions = Object.values(actions).some(Boolean);

  const headerLeft = () => (
    <Header.Start>
      <Header.Button
        accessibilityLabel="Open navigation"
        testID="OpenNavigationButton"
        onPress={openDrawer}
      >
        <Icon name="menu-outline" size="3xl" />
      </Header.Button>
    </Header.Start>
  );

  const headerTitle = () => (
    <Header.TitleContainer>
      <Box flex>
        <Text
          flexShrink={1}
          size="xl"
          numberOfLines={1}
          testID="ScreenHeaderPrimaryTitle"
        >
          {event?.title ?? 'Organization'}
        </Text>
        <Box
          flexShrink={0}
          direction="horizontal"
          alignItems="center"
          space="sm"
        >
          <Icon variant="typographySecondary" name="globe-outline" size="sm" />
          <Text variant="typographySecondary" size="xs">
            Organization
          </Text>
        </Box>
      </Box>
    </Header.TitleContainer>
  );

  const headerRight = () =>
    showActions ? (
      <Header.End>
        <Header.ButtonGroup>
          <Header.Button
            accessibilityLabel="Organization settings"
            onPress={() => {
              navigate({
                pathname: '/organization/[organizationId]/edit',
                params: { organizationId },
              });
            }}
          >
            <Icon name="settings-outline" size="2xl" />
          </Header.Button>
        </Header.ButtonGroup>
      </Header.End>
    ) : null;

  return (
    <Stack.Screen
      options={{
        headerLeft,
        headerTitle,
        headerRight,
        title: event?.title ?? '',
        headerShadowVisible: false,
      }}
    />
  );
}

export default OrganizationDetailToolbar;
